// useNavVisibility — the v3 `layoutStore.checkItem` predicate dispatcher (`layoutStore.ts:273–361`,
// 11 live control types), ported with the LOGIC intact and the COUPLING dead (contract 05 §9):
// v3 reached into `useWorkspaceStore`/`useReportStore`/`useAccountStore`/`useIntegrationStore`
// directly (`:274–277`); v4 reads ONLY typed public seams — `useRbac`/`useFeatureFlags`/
// `useWorkspace` (module 07 barrel), module 08's report surfaces and module 12's integrations
// access surface.
//
// Ported semantics (each has a spec):
//   • fail CLOSED — an unknown/malformed control evaluates false (v3 `:360`); this is what lets
//     the shell cut over with a degraded nav.
//   • `multiple` = OR over nested rules (v3 `:355–358`).
//   • `hidePermission` HIDES when the permission IS present (inverted, v3 `:302`) — and, exactly
//     as in v3, it is only consulted when the item carries a control that is NOT
//     `sysAdmin`/`checkAccountCenter` (those return before the check, v3 `:287–300`), and never
//     on control-less items (v3 `:280`).
//   • `flag` is bypassed for sysadmins (v3 `:327`) — parity, ledger A4; `isSysAdmin` comes from
//     `useRbac()` (the server-authoritative rbac bit). NOTE for rollout owners: the bypass means
//     sysadmins can never see the flagged-OFF experience.
//   • `storeAnalysis` = the `store_analysis` feature flag (ratified C-decision / BE-05-07).
//     The v3 hardcoded production workspace id is dead — never reintroduce it.
import type { NavItem, NavVisibilityRule } from './types'

import { useFeatureFlags, useRbac, useWorkspace } from '@/features/account'
import { useIntegrations } from '@/features/integrations'
import { SUMMARY_REPORT_ID, useReportAccess, useReportSchedules } from '@/features/reports'

// ── Ported domain constants ───────────────────────────────────────────
/** v3 `checkItem:304–318` hardcodes the Product Insights gate: merchant_center + report 17. */
const PRODUCT_INSIGHTS_REPORT_ID = 17
const PRODUCT_INSIGHTS_INTEGRATION = 'merchant_center'
/** Schedule-tab MACHINE KEYS (wire values in `additional_info.tabs`), not user-facing copy. */
const PRODUCT_INSIGHTS_TABS = [
  'overview',
  'Top Merchant View',
  'merchantcenter_competitivevisibility',
  'merchantcenter_bestsellers_product',
]
/** Ratified: the store-analysis rule is served by `useFeatureFlags` under this flag key. */
export const STORE_ANALYSIS_FLAG = 'store_analysis'
/** v3 `checkItem:288` — Account Center needs at least two workspaces. */
const ACCOUNT_CENTER_MIN_WORKSPACES = 2

/**
 * The typed source seam the evaluator reads (contract §9). Plain getters, called inside the
 * consumer's `computed`, so reactivity flows from the underlying stores without this module
 * holding refs. Tests inject a partial fake via `createNavVisibility`.
 */
export interface NavVisibilitySources {
  /** `useRbac().isSysAdmin` — the server-authoritative bit (BE-ACC-01, ledger A4). */
  isSysAdmin: () => boolean
  /** `useRbac().isAccountHolder` — server-computed `rbac.is_account_holder`. */
  isAccountHolder: () => boolean
  hasPermission: (key: string) => boolean
  hasAnyPermission: (keys: string[]) => boolean
  /** `useFeatureFlags().checkFlag`. */
  checkFlag: (key: string) => boolean
  workspaceCount: () => number
  isEcommerceWorkspace: () => boolean
  // — module 08 report access + schedule metadata —
  checkReport: (reportId: number) => boolean
  scheduleTabsFor: (reportId: number) => Record<string, unknown>
  // — module 12 integration access —
  hasIntegration: (name: string) => boolean
}

/** The default seam: public composables from modules 07, 08 and 12. */
export function createDefaultNavVisibilitySources(): NavVisibilitySources {
  const rbac = useRbac()
  const flags = useFeatureFlags()
  const workspace = useWorkspace()
  const integrations = useIntegrations()
  const reportAccess = useReportAccess()
  const reportSchedules = useReportSchedules()

  return {
    isSysAdmin: () => rbac.isSysAdmin.value,
    isAccountHolder: () => rbac.isAccountHolder.value,
    hasPermission: (key) => rbac.hasPermission(key),
    hasAnyPermission: (keys) => rbac.hasAny(keys),
    checkFlag: (key) => flags.checkFlag(key),
    workspaceCount: () => workspace.workspaces.value.length,
    // v3 `!!workspaceStore.workspace?.is_ecommerce` (`:342`); the wire field is boolean | null.
    isEcommerceWorkspace: () => workspace.workspace.value?.['is_ecommerce'] === true,
    checkReport: (reportId) => reportAccess.checkReport(reportId),
    scheduleTabsFor: (reportId) => {
      const tabs = reportSchedules.getScheduleByReportId(reportId)?.additional_info?.tabs
      return typeof tabs === 'object' && tabs !== null ? tabs : {}
    },
    hasIntegration: (name) => integrations.hasIntegration(name),
  }
}

export interface NavVisibility {
  /** Contract §9: `(rule: NavVisibilityRule) => boolean`. */
  isRuleVisible: (rule: NavVisibilityRule) => boolean
  /** The item-level check `buildNavNode` runs (rule + `hidePermission`, v3 ordering). */
  isItemVisible: (item: Pick<NavItem, 'visibility' | 'hidePermission'>) => boolean
}

/** Pure factory over an injected seam — the unit-test surface for all 11 controls. */
export function createNavVisibility(sources: NavVisibilitySources): NavVisibility {
  /** v3 `reportStore.checkReport` (`reportStore.ts:82–90`) on the aggregated sections:
   *  `status === 'Available'` AND a carrying schedule (id 12 exempt) — contract 05 §12. The v3
   *  `is_updating` / workspace-type checks are NOT on the slim wire and are dropped by contract. */
  const isReportVisible = (reportId: number): boolean => sources.checkReport(reportId)

  /** v3 `checkItem:320–325` — raw-first-element short-circuit, then ANY id visible. */
  const evaluateReport = (id: unknown): boolean => {
    const ids = Array.isArray(id) ? id : []
    if (ids[0] === SUMMARY_REPORT_ID) return true
    return ids.some((entry) => typeof entry === 'number' && isReportVisible(entry))
  }

  /** v3 `checkItem:340–343` — ANY id visible AND the workspace is flagged ecommerce. */
  const evaluateReportEcommerce = (id: unknown): boolean => {
    const ids = Array.isArray(id) ? id : []
    return (
      ids.some((entry) => typeof entry === 'number' && isReportVisible(entry)) &&
      sources.isEcommerceWorkspace()
    )
  }

  /** v3 `checkScheduleTabs` as the nav calls it (`checkItem:351–353`): the report's schedule
   *  tab-map contains ANY of the named tab keys (truthy entry). */
  const evaluateScheduleTab = (id: unknown, tabs: string[] | undefined): boolean => {
    if (typeof id !== 'number' || !tabs?.length) return false
    const tabMap = sources.scheduleTabsFor(id)
    return tabs.some((tab) => Boolean(tabMap[tab]))
  }

  /** v3 `checkItem:345` — `getPermission` truthiness: string key or ANY-of key array. */
  const evaluatePermission = (id: unknown): boolean => {
    if (typeof id === 'string') return sources.hasPermission(id)
    if (Array.isArray(id)) {
      return sources.hasAnyPermission(id.filter((key): key is string => typeof key === 'string'))
    }
    return false
  }

  /** v3 `checkItem:346–349` — ANY named integration connected. */
  const evaluateIntegration = (id: unknown): boolean => {
    const ids = Array.isArray(id) ? id : []
    return ids.some((name) => typeof name === 'string' && sources.hasIntegration(name))
  }

  /** v3 `checkItem:326–328` — sysadmin bypass, then the workspace flag. */
  const evaluateFlag = (id: unknown): boolean => {
    if (sources.isSysAdmin()) return true
    return typeof id === 'string' && sources.checkFlag(id)
  }

  /** v3 `checkItem:304–318` — merchant_center connected AND report 17 visible AND the report-17
   *  schedule exposes at least one product tab. */
  const evaluateProductInsights = (): boolean => {
    if (!sources.hasIntegration(PRODUCT_INSIGHTS_INTEGRATION)) return false
    if (!isReportVisible(PRODUCT_INSIGHTS_REPORT_ID)) return false
    const tabMap = sources.scheduleTabsFor(PRODUCT_INSIGHTS_REPORT_ID)
    return PRODUCT_INSIGHTS_TABS.some((tab) => Boolean(tabMap[tab]))
  }

  /**
   * v3 `checkItem:287–299`, re-grounded on the server-authoritative rbac payload: fewer than two
   * workspaces hides it; otherwise the caller must own the selected account or be sysadmin.
   * The v3 owner detection was a CLIENT approximation (caller email == account-owner email and
   * the account absent from the caller's granted list) → replaced by `rbac.is_account_holder`
   * (BE-ACC-01, the server computes `account.user_id == caller.id`). The v3 `isAH` fallback was
   * a browser id-allowlist (`AH_USER_IDS`) — DELETED, not ported: audit ORP-18613 forbids a
   * client id-set deciding authority, and BE-05-01 removed the allowlists wholesale.
   */
  const evaluateAccountCenter = (): boolean => {
    if (sources.workspaceCount() < ACCOUNT_CENTER_MIN_WORKSPACES) return false
    return sources.isAccountHolder() || sources.isSysAdmin()
  }

  const evaluate = (rule: NavVisibilityRule, hidePermission?: string): boolean => {
    // v3 ordering (`:287–302`): these two controls return BEFORE the hidePermission check.
    if (rule.control === 'checkAccountCenter') return evaluateAccountCenter()
    if (rule.control === 'sysAdmin') return sources.isSysAdmin()

    // Inverted semantics (v3 `:302`): the permission's PRESENCE hides the item.
    if (hidePermission && sources.hasPermission(hidePermission)) return false

    switch (rule.control) {
      case 'productInsights':
        return evaluateProductInsights()
      case 'report':
        return evaluateReport(rule.id)
      case 'flag':
        return evaluateFlag(rule.id)
      case 'storeAnalysis':
        // Ratified (BE-05-07): the flag path — identical to `{control:'flag', id:'store_analysis'}`.
        return evaluateFlag(STORE_ANALYSIS_FLAG)
      case 'reportEcommerce':
        return evaluateReportEcommerce(rule.id)
      case 'permission':
        return evaluatePermission(rule.id)
      case 'integration':
        return evaluateIntegration(rule.id)
      case 'scheduleTab':
        return evaluateScheduleTab(rule.id, rule.tabs)
      case 'static':
        // Listed unconditionally — the hidePermission check above is its only gate (ORP-26799).
        return true
      case 'multiple':
        // OR semantics (v3 `:355–358`). Nested entries are RULES; malformed ones fail closed.
        return (
          Array.isArray(rule.id) &&
          rule.id.some((nested) => isNestedRule(nested) && isRuleVisible(nested))
        )
      default:
        // Fail closed (v3 `:360`) — unknown controls hide, so a degraded nav never over-shows.
        return false
    }
  }

  const isRuleVisible = (rule: NavVisibilityRule): boolean => evaluate(rule)

  const isItemVisible = (item: Pick<NavItem, 'visibility' | 'hidePermission'>): boolean => {
    // v3 `:280` + `buildNode:410`: control-less items are always visible — `hidePermission`
    // alone has no effect (parity; e.g. the Cockpit parent relies on this).
    // v3's `bottom → true` short-circuit (`:279`) is dropped with the field: its ONE user was a
    // component-local Settings item (`SideNavItems.vue:351`), which never flows through here —
    // the settings region composes through module 22's entry registry, and `SettingsNavTree`
    // runs the resulting items back through THIS engine (ORP-19896).
    if (!item.visibility) return true
    return evaluate(item.visibility, item.hidePermission)
  }

  return { isRuleVisible, isItemVisible }
}

function isNestedRule(value: unknown): value is NavVisibilityRule {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { control?: unknown }).control === 'string'
  )
}

/**
 * Contract §9 composable: `useNavVisibility()` → `(rule) => boolean`, reading only feature
 * public composables. Call inside a component/composable `setup` (needs the active Pinia).
 */
export function useNavVisibility(): (rule: NavVisibilityRule) => boolean {
  return createNavVisibility(createDefaultNavVisibilitySources()).isRuleVisible
}
